import { useLoaderData } from "@remix-run/react";
import * as routeUtils from "~/contentful/route-utils";
import { LoaderFunction } from "@remix-run/server-runtime";
import invariant from "tiny-invariant";
import { IInsight, ITag, SpecificLocale } from "~/@types/generated/contentful";
import { getEntryBySlug, getLinksToEntry } from "~/contentful/index.server";
import { i18n } from "~/i18n.server";
import ColorHero from "~/components/heros/ColorHero";
import {
  ComponentDataProvider,
  PageComponentData,
} from "~/contexts/ComponentDataProvider";
import CardList from "~/components/Cards/CardList";
import { FourOFourCaughtData } from "~/components/shared/FourOFour";
import { ClientSide } from "~/@types";
import clamp from "lodash/clamp";
import { INSIGHT_CARD_GROUP_SIZE } from "~/config";
import QueryString from "qs";
import SchemaOrg from "~/components/utils/SchemaOrg";
import {
  SchemaOrgType,
  tbiOrganizationSchema,
} from "~/components/utils/schemaOrgUtils.server";

export const meta = routeUtils.meta;

export const handle = routeUtils.handle("tag", "tags");

interface LoaderData {
  tag: SpecificLocale<ITag>;
  componentData: PageComponentData;
  meta: routeUtils.MetaInfo;
  schemaOrg: SchemaOrgType;
}

export const loader: LoaderFunction = async ({ params, request }) => {
  const locale = await i18n.getLocale(request);

  const invariantMessage =
    "expected slug to be passed as an argument or params.slug to be defined";
  const slugToQuery = params.tagSlug;

  invariant(slugToQuery, invariantMessage);

  const tag = (await getEntryBySlug("tag", slugToQuery, {
    locale,
  })) as SpecificLocale<ITag>;

  if (params.tagSlug && !tag) {
    throw new Response(
      // This is me cheating as a way to pass the color through
      JSON.stringify({
        message: "Tag Not Found",
        backgroundColor: "white",
        notFound: "tag",
      } as FourOFourCaughtData),
      { status: 404 }
    );
  }

  const url = new URL(request.url);
  const searchParams = QueryString.parse(url.search.slice(1));
  const page = searchParams.page ? Number(searchParams.page) : 1;

  const collection = await getLinksToEntry(tag.sys.id, locale, {
    content_type: "insight",
    order: "-fields.date",
    limit: clamp(page * INSIGHT_CARD_GROUP_SIZE, 0, 96),
    select: [
      "sys.id",
      "fields.title",
      "fields.slug",
      "fields.image",
      "fields.experts",
      "fields.topic",
      "fields.date",
      "fields.embargoed",
    ],
    excludeEmbargoed: true,
  });
  const schemaUrl = new URL(request.url);
  schemaUrl.search = "";
  schemaUrl.hash = "";
  const schemaOrg: SchemaOrgType = {
    "@context": "https://schema.org",
    "@type": "Collection",
    about: {
      name: tag.fields.title,
      alternateName: tag.fields.slug,
      description: `Listing page for insights tagged "${tag.fields.title}"`,
    },
    isAccessibleForFree: true,
    url: schemaUrl.toString(),
    publisher: tbiOrganizationSchema,
    sourceOrganization: tbiOrganizationSchema,
  };

  let componentData: PageComponentData = {
    insightsList: {
      [tag.sys.id]: collection.items as ClientSide<IInsight>[],
    },
    totalInsights: collection.total,
  };

  return {
    tag,
    componentData,
    meta: routeUtils.getMetaInfo({
      title: tag.fields.title,
      description: `Insight tag - ${tag.fields.title}`,
      requestUrl: request.url,
    }),
    schemaOrg,
  } as LoaderData;
};

export default function TagPage() {
  const { tag, componentData, schemaOrg } = useLoaderData<LoaderData>();
  return (
    <ComponentDataProvider componentData={componentData}>
      <SchemaOrg content={schemaOrg} />
      <ColorHero sysId={tag.sys.id} tag={tag.fields} />
      <CardList relatedEntry={tag} />
    </ComponentDataProvider>
  );
}

export const ErrorBoundary = routeUtils.ContentfulErrorBoundary;

export const CatchBoundary = routeUtils.ContentfulCatchBoundary;

export const shouldRevalidate = routeUtils.shouldRevalidate;
